import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Camera, Phone, ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react';
const OPTIONS = [{
  id: 'replace',
  label: 'Replace my existing mailbox',
  recommendation: {
    title: 'Home Mailboxes',
    shopLink: '/6m8/mailboxes.html',
    checkText: 'Check if your current post is reusable and note your mounting type (curbside vs wall).'
  }
}, {
  id: 'new',
  label: 'Buy a new mailbox system',
  recommendation: {
    title: 'Complete Mailbox & Post Systems',
    shopLink: '/6m8/mailboxes/complete-mailbox-post-system.html',
    checkText: 'Look for complete post systems and matching address plaques for a cohesive look.'
  }
}, {
  id: 'commercial',
  label: 'Plan a commercial/community delivery setup',
  recommendation: {
    title: 'Commercial Mailboxes & Lockers',
    shopLink: '/6m8/commercial-mailboxes-lockers.html',
    checkText: 'Confirm USPS approval requirements and total door count needed before ordering.'
  }
}, {
  id: 'part',
  label: 'Find a part, number, lock, or door',
  recommendation: {
    title: 'Replacement Parts',
    shopLink: '/6m8/mailboxes/mailbox-replacement-parts.html',
    checkText: 'Find the manufacturer name on your current box to ensure part compatibility.'
  }
}, {
  id: 'hoa',
  label: 'Match an HOA or existing model',
  recommendation: {
    title: 'Commercial Mailboxes & Lockers',
    shopLink: '/6m8/commercial-mailboxes-lockers.html',
    checkText: 'Have your HOA guidelines ready, specifically noting required colors, finials, or numbering styles.'
  }
}];
export function GuidedBuyer() {
  const [selectedId, setSelectedId] = useState<string | null>('replace');
  const selectedOption = OPTIONS.find(opt => opt.id === selectedId);
  return <section className="py-16 lg:py-24 bg-brand-cream border-b border-brand-border" data-id="element-178">
      <div className="max-w-7xl mx-auto px-4" data-id="element-179">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-180">
          {/* Left Column: Context */}
          <div data-id="element-181">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-brand-border text-brand-green text-sm font-bold tracking-wide mb-6 uppercase shadow-sm" data-id="element-182">
              <HelpCircle size={16} data-id="element-183" /> Expert Guidance
            </div>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-184">
              Not sure what fits? We'll help narrow it down.
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-185">
              Mailboxes can look similar online. The right choice depends on
              delivery type, USPS requirements, post compatibility, finish,
              numbering, installation, and what needs to match your property.
            </p>

            <div className="bg-white p-5 rounded-xl border border-brand-border mb-8" data-id="element-186">
              <h4 className="font-bold text-brand-charcoal mb-3 text-sm uppercase tracking-wider" data-id="element-187">
                What to have ready:
              </h4>
              <ul className="text-sm text-gray-600 space-y-2" data-id="element-188">
                <li className="flex items-center gap-2" data-id="element-189">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-190" /> Photos
                  of current mailbox
                </li>
                <li className="flex items-center gap-2" data-id="element-191">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-192" />{' '}
                  Manufacturer/model if visible
                </li>
                <li className="flex items-center gap-2" data-id="element-193">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-194" /> HOA
                  requirement if applicable
                </li>
                <li className="flex items-center gap-2" data-id="element-195">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-196" />{' '}
                  Delivery type / property type
                </li>
                <li className="flex items-center gap-2" data-id="element-197">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-198" /> Install
                  ZIP/location
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-199">
              <a href="/ContactUs.html" className="bg-brand-green hover:bg-brand-green/90 text-white px-6 py-3 rounded-md font-semibold text-center transition-colors shadow-sm" data-id="element-200">
                Get help choosing
              </a>
              <a href="/ContactUs.html" className="bg-white border border-brand-border hover:border-brand-green text-brand-charcoal px-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm" data-id="element-201">
                <Camera size={18} className="text-brand-green" data-id="element-202" /> Send photos
                for help
              </a>
            </div>

            <div className="mt-8 flex items-center gap-3 text-gray-600 font-medium bg-white/50 inline-flex px-4 py-2 rounded-lg border border-brand-border/50" data-id="element-203">
              <Phone size={18} className="text-brand-green" data-id="element-204" />
              <span data-id="element-205">
                Prefer to talk? Call{' '}
                <a href="tel:301-620-7585" className="text-brand-green hover:text-brand-red transition-colors" data-id="element-206">
                  301-620-7585
                </a>
              </span>
            </div>
          </div>

          {/* Right Column: Interactive Selector */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-border p-6 lg:p-8" data-id="element-207">
            <h3 className="text-xl font-serif font-bold text-brand-charcoal mb-6" data-id="element-208">
              What are you trying to do?
            </h3>

            <div className="space-y-3" data-id="element-209">
              {OPTIONS.map(option => <button key={option.id} onClick={() => setSelectedId(option.id)} className={`w-full text-left px-5 py-4 rounded-xl border transition-all duration-200 flex items-center justify-between group ${selectedId === option.id ? 'border-brand-green bg-brand-cream shadow-sm' : 'border-brand-border hover:border-brand-green/50 hover:bg-brand-cream/50'}`} data-id="element-210">
                  <span className={`font-medium ${selectedId === option.id ? 'text-brand-green' : 'text-brand-charcoal'}`} data-id="element-211">
                    {option.label}
                  </span>
                  <ChevronRight size={20} className={selectedId === option.id ? 'text-brand-green' : 'text-gray-400 group-hover:text-brand-green'} data-id="element-212" />
                </button>)}
            </div>

            {/* Recommendation Panel */}
            <AnimatePresence mode="wait" data-id="element-213">
              {selectedOption && <motion.div key={selectedOption.id} initial={{
              opacity: 0,
              height: 0,
              marginTop: 0
            }} animate={{
              opacity: 1,
              height: 'auto',
              marginTop: 24
            }} exit={{
              opacity: 0,
              height: 0,
              marginTop: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-214">
                  <div className="bg-brand-cream rounded-xl p-6 border border-brand-border" data-id="element-215">
                    <h4 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-216">
                      <CheckCircle2 size={18} className="text-brand-green" data-id="element-217" />{' '}
                      Recommended Next Steps
                    </h4>

                    <div className="bg-white p-4 rounded-lg border border-brand-border/50 mb-5" data-id="element-218">
                      <p className="text-sm text-gray-700" data-id="element-219">
                        <strong className="text-brand-charcoal block mb-1" data-id="element-220">
                          What to check before ordering:
                        </strong>
                        {selectedOption.recommendation.checkText}
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-221">
                      <a href={selectedOption.recommendation.shopLink} className="flex-1 bg-brand-red hover:bg-brand-red/90 text-white text-center py-2.5 rounded font-medium text-sm transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-222">
                        Shop this category <ArrowRight size={16} data-id="element-223" />
                      </a>
                      <a href="/ContactUs.html" className="flex-1 bg-white border border-brand-border hover:border-brand-green text-brand-charcoal text-center py-2.5 rounded font-medium text-sm transition-colors" data-id="element-224">
                        Ask MidAtlantic to review
                      </a>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}